import React, {useEffect, useState} from 'react';
import {View} from 'react-native';

import Checkbox from '/component/base/Checkbox';
import Icon from '/component/base/Icon';
import LoadingIndicator from '/component/base/LoadingIndicator';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import ProfileSection from '/component/partial/profile/ProfileSection';
import candidateReminderTestIDs from '/constant/TestIDs/CandidateReminder';
import {DAILY, DOES_NOT_REPEAT} from '/enum/reminders.enum';
import {useTranslation} from '/provider/TranslationProvider';
import testProperties from '/util/testing/testProperties';

import {
  combineDateWithTime,
  formatDate,
  getNextReminderDate,
  getRepeatText,
  isReminderTimePassed,
  resetToMidnight,
  shouldShowCheckbox,
  toLocalDate,
} from '../ReminderUtils/ReminderUtils.util';

import styles from './CheckedInReminder.style';

export interface CheckedInReminderProps {
  date: string;
  time: string;
  frequency: string;
  onToggleClick: (newCheckedState: boolean) => void;
  onReminderClick: () => void;
  startDate: string;
  checkState: boolean;
  checkDate: string | null;
  hasReminderDetails: boolean;
  endDate: string;
}

const CheckedInReminder: React.FC<CheckedInReminderProps> = ({
  date,
  time,
  onToggleClick,
  onReminderClick,
  startDate,
  checkState,
  checkDate,
  frequency,
  hasReminderDetails,
  endDate,
}) => {
  const {tt} = useTranslation('CheckInReminder');
  const [isChecked, setIsChecked] = useState(checkState || false);
  const [reminderDate, setReminderDate] = useState<string | null>(null);
  const [nextReminderDate, setNextReminderDate] = useState<Date | null>(null);
  const [currentDate, setCurrentDate] = useState(resetToMidnight(new Date()));
  const [hasReminderTimePassedState, setHasReminderTimePassedState] =
    useState(false);
  const [checkboxReset, setCheckboxReset] = useState(false);

  // Update the local state when checkState prop changes
  useEffect(() => {
    setIsChecked(checkState);
  }, [checkState]);

  // Calculate the next reminder date based on frequency and set reminderDate
  useEffect(() => {
    if (startDate) {
      const newNextReminderDate = getNextReminderDate(startDate, frequency);
      setNextReminderDate(newNextReminderDate);
    }
  }, [date, frequency, startDate]);

  useEffect(() => {
    if (startDate) {
      // Compare the startDate with today's date
      const isStartDateToday =
        toLocalDate(currentDate) === toLocalDate(new Date(startDate));
      if (
        isStartDateToday &&
        checkDate &&
        toLocalDate(checkDate) < toLocalDate(currentDate)
      ) {
        setIsChecked(false); // Reset checkbox to false
      }
      setReminderDate(startDate);
    }
  }, [startDate, checkDate, currentDate]);

  useEffect(() => {
    if (!checkDate) return;

    const currentDateStr = toLocalDate(currentDate);
    const checkDateStr = toLocalDate(checkDate);

    // Reset when the current day is greater than checkDate (i.e., the next day has come)
    if (checkDateStr < currentDateStr && !checkboxReset) {
      setIsChecked(false);
      setCheckboxReset(true);
    } else if (checkDateStr === currentDateStr) {
      setCheckboxReset(false);
    }
  }, [currentDate, checkDate]);

  useEffect(() => {
    if (checkDate && toLocalDate(checkDate) < toLocalDate(new Date())) {
      if (frequency === DAILY) {
        const dateToUse =
          startDate && new Date(startDate) > new Date()
            ? startDate // Use startDate if it's a future date
            : currentDate; // Otherwise, use currentDate
        // If not checked, update reminderDate for the next day
        const newReminderDate = combineDateWithTime(
          new Date(dateToUse),
          time,
          true,
        );

        if (
          newReminderDate &&
          reminderDate &&
          new Date(newReminderDate).getTime() !==
            new Date(reminderDate).getTime()
        ) {
          setReminderDate(newReminderDate.toISOString()); // Update reminder date for the next day
        }
      }
      if (
        nextReminderDate &&
        frequency !== DAILY &&
        frequency !== DOES_NOT_REPEAT &&
        toLocalDate(nextReminderDate) === toLocalDate(new Date())
      ) {
      }
    }

    const currentDateString = toLocalDate(new Date());
    const nextReminderDateString =
      nextReminderDate && toLocalDate(nextReminderDate);
    const startDateString = toLocalDate(startDate);
    const endDateString = toLocalDate(endDate);
    // If nextReminderDate exceeds endDate or endDate is reached
    if (
      nextReminderDateString &&
      frequency !== DOES_NOT_REPEAT &&
      (nextReminderDateString > endDateString ||
        currentDateString === endDateString)
    ) {
      // If it's checked on the endDate, do not change reminderDate, but keep isChecked true
      if (isChecked && currentDateString === endDateString) {
        // Do not change reminderDate, keep it as it is
        setIsChecked(true);
      } else {
        // Reset reminderDate and set isChecked to false when reminder is no longer active
        setReminderDate(endDate);
        setIsChecked(false); // Optional: adjust based on your logic
      }
      return;
    }
    if (
      nextReminderDateString === currentDateString &&
      nextReminderDateString > startDateString
    ) {
      const newReminderDate = combineDateWithTime(currentDate, time, true);

      if (
        newReminderDate &&
        reminderDate &&
        new Date(newReminderDate).getTime() !== new Date(reminderDate).getTime()
      ) {
        setReminderDate(newReminderDate.toISOString());
      }
    }
  }, [
    checkDate,
    currentDate,
    nextReminderDate,
    reminderDate,
    isChecked,
    time,
    endDate,
    frequency,
    startDate,
  ]);

  const getReminderDate = () => {
    const currentDateString = toLocalDate(new Date());
    const endDateString = toLocalDate(endDate);
    const nextReminderDateString =
      nextReminderDate && toLocalDate(nextReminderDate);
    const reminderDateString =
      reminderDate && nextReminderDate && toLocalDate(reminderDate);

    // If endDate is less than or equal to today, return endDate as reminder date
    if (endDate && endDateString <= currentDateString) {
      return formatDate(new Date(endDateString));
    }

    // If the reminder is for the future, return that reminder date.

    if (reminderDateString && reminderDateString > currentDateString) {
      return formatDate(new Date(reminderDateString));
    }

    // If isChecked is false and frequency is not DAILY or DOES_NOT_REPEAT, check if today is nextReminderDate
    if (!isChecked && frequency !== DAILY && frequency !== DOES_NOT_REPEAT) {
      if (nextReminderDate && currentDateString === nextReminderDateString) {
        return formatDate(nextReminderDate);
      }
    }

    // If isChecked and nextReminderDate exist, and frequency is not 'DOES_NOT_REPEAT'
    if (isChecked && nextReminderDate && frequency !== DOES_NOT_REPEAT) {
      return formatDate(nextReminderDate);
    }

    // If not checked and frequency is DAILY, return the combined reminder date
    if (!isChecked && frequency === DAILY && reminderDateString) {
      return formatDate(new Date(reminderDateString));
    }
    // Return the default date
    return date;
  };

  useEffect(() => {
    // Get the current time
    const now = new Date();

    // Create a new Date object for the next midnight (next day at 00:00:00)
    const nextMidnight = new Date(now);
    nextMidnight.setHours(24, 0, 0, 0); // Set to next midnight (00:00:00 of the next day)

    // Calculate the time difference until the next midnight in milliseconds
    const timeUntilMidnight = nextMidnight.getTime() - now.getTime();

    // Set timeout to trigger at midnight
    const timeoutId = setTimeout(() => {
      const today = resetToMidnight(new Date()); // Reset to the new date
      setCurrentDate(today); // Update the state to the new date
    }, timeUntilMidnight);

    // Cleanup timeout on unmount or when dependencies change
    return () => clearTimeout(timeoutId);
  }, [currentDate]);

  const getDisabledIconStyle = (valueIsChecked: boolean) => {
    return valueIsChecked ? styles.selectedIcon : styles.unSelectedIcon;
  };

  const getTaskItemIcon = (valueIsChecked: boolean) => {
    return valueIsChecked ? 'checkbox-selected' : 'checkbox-unselected';
  };

  const isCheckboxVisible = shouldShowCheckbox(
    reminderDate,
    nextReminderDate,
    checkDate,
    frequency,
    isChecked,
    endDate,
  );

  useEffect(() => {
    const reminderTimePassed = isReminderTimePassed(
      reminderDate,
      nextReminderDate,
      frequency,
      isChecked,
      checkDate,
    );
    setHasReminderTimePassedState(reminderTimePassed);
  }, [
    reminderDate,
    nextReminderDate,
    frequency,
    isChecked,
    checkDate,
    currentDate,
  ]);

  const toggleCheckbox = () => {
    if (!isChecked) {
      // When checking the checkbox, set the next reminder date
      const updatedNextReminderDate = reminderDate
        ? getNextReminderDate(reminderDate, frequency)
        : null;
      setNextReminderDate(updatedNextReminderDate);
      setIsChecked(true);
      setTimeout(() => {
        onToggleClick(true);
      }, 50);
    } else {
      setReminderDate(reminderDate ?? null);
      setIsChecked(false);
      setTimeout(() => {
        onToggleClick(false);
      }, 50);
    }
  };

  if (!hasReminderDetails) {
    return <LoadingIndicator size="small" style={styles.loader} />;
  }

  return (
    <ProfileSection
      title={tt('heading', 'Check-in Reminder')}
      analyticsTitle="Check-in Reminder"
      style={{base: styles.container, titleText: styles.titleText}}>
      <View testID={candidateReminderTestIDs.checkedInReminderView}>
        <TouchableOpacity
          style={styles.reminderContainer}
          onPress={onReminderClick}
          testID={candidateReminderTestIDs.reminderTouchableArea}>
          {isCheckboxVisible && (
            <View style={styles.iconContainer}>
              <Checkbox
                {...testProperties(candidateReminderTestIDs.checkOffCheckbox)}
                testID={candidateReminderTestIDs.checkOffCheckbox}
                checkedIconName={getTaskItemIcon(isChecked)}
                checked={isChecked}
                style={{icon: getDisabledIconStyle(isChecked)}}
                onChange={toggleCheckbox}
                rectangleCheckbox
              />
            </View>
          )}

          <View style={styles.labelsContainer}>
            <Text
              variant="heading"
              {...testProperties(candidateReminderTestIDs.dateLabel)}
              testID={candidateReminderTestIDs.dateLabel}
              style={
                !isChecked && hasReminderTimePassedState
                  ? styles.pastLabelDate
                  : styles.labelDate
              }>
              {`${getReminderDate()} at ${time}`}
            </Text>
            <Text
              {...testProperties(candidateReminderTestIDs.repeatLabel)}
              testID={candidateReminderTestIDs.repeatLabel}
              variant="body"
              style={styles.labelRepeat}>
              {getRepeatText(frequency)}
            </Text>
          </View>

          <Icon name={'chevron-right'} testID="icon" />
        </TouchableOpacity>
      </View>
    </ProfileSection>
  );
};

export default CheckedInReminder;
